import { useMemo, useRef } from 'react';

import { Box, Button, Divider, Flex, FormLabel, Text } from '@chakra-ui/react';
import { Formik, FormikProps } from 'formik';
import { toast } from 'react-toastify';

import {
  AssignmentMode,
  UpdateUserSettingsMutationVariables,
  User,
  UserType,
  useUpdateUserSettingsMutation,
} from '../../gql/generated/graphql';

import Modal from '../../components/shared/Modal';
import NumberInput from '../../components/shared/NumberInput';
import Select from '../../components/shared/Select';
import Switch from '../../components/shared/Switch';

import WorkingHoursTable from './WorkingHoursTable/WorkingHoursTable';
import {
  UserSettingsFormikFields,
  getWorkingHoursFields,
  getWorkingHoursString,
  userSettingsValidationSchema,
} from './utils';

import { PermissionEnum } from '../../constants/permissions';
import {
  DEFAULT_ASSIGNMENT_MODE,
  DEFAULT_FREQUENCY_MINUTES,
  DEFAULT_USER_TYPE,
  MIN_MAX_AUTO_ASSIGN_DEALS,
} from '../../constants/user';
import { useHasPermission } from '../../hooks/useHasPermission';
import { logger } from '../../libs/Logger';

type UserSettingsModalProps = {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  refetchUsers: () => Promise<unknown>;
};

type SwitchConfig = {
  name: string;
  label: string;
};

const mainSettingsSwitches: SwitchConfig[] = [
  { name: 'auto_assign_deals', label: 'Auto Assign Deals' },
  { name: 'on_vacation', label: 'On Vacation' },
];

const dealAcceptanceSwitches: SwitchConfig[] = [
  { name: 'autoAssignEligibility.accept_overnight_deals', label: 'Overnight Deals' },
  { name: 'autoAssignEligibility.accept_ai_touched_deals', label: 'AI Touched Deals' },
  { name: 'autoAssignEligibility.accept_unseen_deals', label: 'Unseen Deals' },
  {
    name: 'autoAssignEligibility.accept_with_upcoming_appointment',
    label: 'Upcoming Appointment',
  },
];

const dealTypeSwitches: SwitchConfig[] = [
  { name: 'autoAssignEligibility.deals_with_vsc', label: 'VSC' },
  { name: 'autoAssignEligibility.deals_with_gap', label: 'GAP' },
  { name: 'autoAssignEligibility.deals_with_stipulations', label: 'Stipulations' },
  { name: 'autoAssignEligibility.deals_with_double_tax', label: 'Double Tax' },
  { name: 'autoAssignEligibility.deals_with_direct_pay', label: 'Direct Pay' },
  { name: 'autoAssignEligibility.deals_with_bank_fees', label: 'Bank Fees' },
  { name: 'autoAssignEligibility.deals_with_down_payment', label: 'Down Payment' },
];

const UserSettingsModal = ({ user, isOpen, onClose, refetchUsers }: UserSettingsModalProps) => {
  const formRef = useRef<FormikProps<UserSettingsFormikFields>>(null);
  const hasUseAiCloserPermission = useHasPermission(PermissionEnum.UseAiCloser);

  const [updateUserSettings, { loading }] = useUpdateUserSettingsMutation();

  const initialValues = useMemo(
    (): UserSettingsFormikFields => ({
      workingHours: getWorkingHoursFields(user?.hours ?? undefined),
      on_vacation: user?.on_vacation ?? false,
      auto_assign_deals: user?.auto_assign_deals ?? false,
      max_auto_assign_deals: user?.max_auto_assign_deals ?? MIN_MAX_AUTO_ASSIGN_DEALS,
      user_type: user?.user_type ?? DEFAULT_USER_TYPE,
      assignment_mode: user?.assignment_mode ?? DEFAULT_ASSIGNMENT_MODE,
      frequency_minutes: user?.frequency_minutes ?? DEFAULT_FREQUENCY_MINUTES,
      autoAssignEligibility: {
        accept_overnight_deals: user?.autoAssignEligibility?.accept_overnight_deals ?? false,
        accept_unseen_deals: user?.autoAssignEligibility?.accept_unseen_deals ?? false,
        accept_with_upcoming_appointment:
          user?.autoAssignEligibility?.accept_with_upcoming_appointment ?? false,
        accept_ai_touched_deals:
          user?.autoAssignEligibility?.accept_ai_touched_deals ?? user?.user_type === UserType.Ai
            ? false
            : user?.autoAssignEligibility?.accept_ai_touched_deals ?? true,
        deals_with_stipulations: user?.autoAssignEligibility?.deals_with_stipulations ?? true,
        deals_with_vsc: user?.autoAssignEligibility?.deals_with_vsc ?? true,
        deals_with_gap: user?.autoAssignEligibility?.deals_with_gap ?? true,
        deals_with_double_tax: user?.autoAssignEligibility?.deals_with_double_tax ?? true,
        deals_with_direct_pay: user?.autoAssignEligibility?.deals_with_direct_pay ?? true,
        deals_with_bank_fees: user?.autoAssignEligibility?.deals_with_bank_fees ?? true,
        deals_with_down_payment: user?.autoAssignEligibility?.deals_with_down_payment ?? true,
      },
    }),
    [user],
  );

  const handleClose = () => {
    onClose();
    formRef.current?.resetForm();
  };

  const onSubmit = async (values: UserSettingsFormikFields) => {
    const variables: UpdateUserSettingsMutationVariables = {
      id: user?.id ?? '',
      hours: getWorkingHoursString(values.workingHours),
      on_vacation: values.on_vacation,
      auto_assign_deals: values.auto_assign_deals,
      max_auto_assign_deals: values.max_auto_assign_deals,
      user_type: values.user_type,
      assignment_mode: values.assignment_mode,
      frequency_minutes: values.frequency_minutes,
      accept_overnight_deals: values.autoAssignEligibility.accept_overnight_deals,
      accept_unseen_deals: values.autoAssignEligibility.accept_unseen_deals,
      accept_with_upcoming_appointment:
        values.autoAssignEligibility.accept_with_upcoming_appointment,
      accept_ai_touched_deals: values.autoAssignEligibility.accept_ai_touched_deals,
      deals_with_stipulations: values.autoAssignEligibility.deals_with_stipulations,
      deals_with_vsc: values.autoAssignEligibility.deals_with_vsc,
      deals_with_gap: values.autoAssignEligibility.deals_with_gap,
      deals_with_double_tax: values.autoAssignEligibility.deals_with_double_tax,
      deals_with_direct_pay: values.autoAssignEligibility.deals_with_direct_pay,
      deals_with_bank_fees: values.autoAssignEligibility.deals_with_bank_fees,
      deals_with_down_payment: values.autoAssignEligibility.deals_with_down_payment,
    };

    await updateUserSettings({
      variables,
      onCompleted: async () => {
        toast.success('User updated successfully');
        handleClose();
        await refetchUsers();
      },
      onError: (error) => {
        logger.error(
          'src/pages/Pods/Components/UserSettingsModal.tsx',
          'updateUserHoursAndOnVacation',
          variables,
          error,
        );
        toast.error(`Error updating User: ${error.message}`);
      },
    });
  };

  return (
    <Formik
      innerRef={formRef}
      initialValues={initialValues}
      validationSchema={userSettingsValidationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ values, isValid, resetForm, setFieldValue }) => {
        const isAiUser = values.user_type === UserType.Ai;
        const aiTouchedDealsFieldName = 'autoAssignEligibility.accept_ai_touched_deals';

        if (isAiUser && values.autoAssignEligibility.accept_ai_touched_deals !== false) {
          setFieldValue(aiTouchedDealsFieldName, false);
        }

        return (
          <Modal
            title={`${user?.name ?? 'User'} Settings`}
            size={{ base: 'dynamicFull', md: 'xl' }}
            variant="noMargin"
            isOpen={isOpen}
            onClose={() => {
              handleClose();
              resetForm();
            }}
            canDismiss={!loading}
            leftButtons={
              <Button
                variant="warning"
                onClick={() => {
                  handleClose();
                  resetForm();
                }}
                isDisabled={loading}
              >
                CANCEL
              </Button>
            }
            rightButtons={
              <Button
                onClick={() => onSubmit(values)}
                isLoading={loading}
                isDisabled={!isValid}
                loadingText="SAVE CHANGES"
              >
                SAVE CHANGES
              </Button>
            }
          >
            <Flex direction="column" gap={3} p={4}>
              {/* Main Settings Section */}
              <Box>
                <Flex direction="column" gap={2}>
                  {mainSettingsSwitches.map((switchConfig, index) => (
                    <Flex
                      key={switchConfig.name}
                      justifyContent="space-between"
                      gap={2}
                      mb={index < mainSettingsSwitches.length - 1 ? 1 : 0}
                    >
                      <FormLabel fontSize="sm" mb={0}>
                        {switchConfig.label}
                      </FormLabel>
                      <Box>
                        <Switch
                          isNoYes
                          noLabel="Off"
                          yesLabel="On"
                          size="sm"
                          noYesFontSize="sm"
                          name={switchConfig.name}
                        />
                      </Box>
                    </Flex>
                  ))}
                </Flex>
              </Box>

              {values.auto_assign_deals ? (
                <>
                  <Divider />

                  {hasUseAiCloserPermission ? (
                    <>
                      {/* AI Closer Settings */}
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={3} color="gray.700">
                          AI Closer Settings
                        </Text>
                        <Flex direction="column" gap={3}>
                          <Flex justifyContent="space-between" alignItems="center" gap={2}>
                            <FormLabel fontSize="sm" mb={0}>
                              User Type
                            </FormLabel>
                            <Box width="150px">
                              <Select
                                name="user_type"
                                options={[
                                  { value: UserType.Human, label: 'Human' },
                                  { value: UserType.Ai, label: 'AI' },
                                ]}
                                emptyOption={false}
                                size="sm"
                              />
                            </Box>
                          </Flex>

                          <Flex justifyContent="space-between" alignItems="center" gap={2}>
                            <FormLabel fontSize="sm" mb={0}>
                              Assignment Mode
                            </FormLabel>
                            <Box width="150px">
                              <Select
                                name="assignment_mode"
                                options={[
                                  { value: AssignmentMode.MaxDeals, label: 'Max Deals' },
                                  { value: AssignmentMode.Frequency, label: 'Frequency' },
                                ]}
                                emptyOption={false}
                                size="sm"
                              />
                            </Box>
                          </Flex>

                          {values.assignment_mode === AssignmentMode.MaxDeals ? (
                            <Flex justifyContent="space-between" alignItems="center" gap={2}>
                              <FormLabel fontSize="sm" mb={0}>
                                Individual Max
                              </FormLabel>
                              <Box width="150px">
                                <NumberInput name="max_auto_assign_deals" width="full" isWhole />
                              </Box>
                            </Flex>
                          ) : null}

                          {values.assignment_mode === AssignmentMode.Frequency ? (
                            <Flex justifyContent="space-between" alignItems="center" gap={2}>
                              <FormLabel fontSize="sm" mb={0}>
                                Frequency (Minutes)
                              </FormLabel>
                              <Box width="150px">
                                <NumberInput name="frequency_minutes" width="full" isWhole />
                              </Box>
                            </Flex>
                          ) : null}
                        </Flex>
                      </Box>
                    </>
                  ) : (
                    <>
                      {/* Individual Max (for users without UseAiCloser permission) */}
                      <Box>
                        <Flex justifyContent="space-between">
                          <FormLabel fontSize="sm" mb={0}>
                            Individual Max
                          </FormLabel>
                          <Box>
                            <NumberInput name="max_auto_assign_deals" width="24" isWhole />
                          </Box>
                        </Flex>
                      </Box>
                    </>
                  )}

                  {hasUseAiCloserPermission ? (
                    <>
                      <Divider />

                      {/* User Eligibility */}
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={3} color="gray.700">
                          User Eligibility
                        </Text>
                        <Flex direction="column" gap={2}>
                          {dealAcceptanceSwitches.map((switchConfig) => {
                            const isAiTouchedDealsSwitch =
                              switchConfig.name === aiTouchedDealsFieldName;
                            return (
                              <Flex key={switchConfig.name} justifyContent="space-between">
                                <FormLabel fontSize="sm" mb={0}>
                                  {switchConfig.label}
                                </FormLabel>
                                <Box>
                                  <Switch
                                    isNoYes
                                    noLabel="No"
                                    yesLabel="Yes"
                                    size="sm"
                                    noYesFontSize="sm"
                                    name={switchConfig.name}
                                    switchProps={
                                      isAiTouchedDealsSwitch
                                        ? {
                                            isDisabled: isAiUser,
                                          }
                                        : undefined
                                    }
                                  />
                                </Box>
                              </Flex>
                            );
                          })}
                        </Flex>
                      </Box>

                      <Divider />

                      {/* Deal Eligibility */}
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={3} color="gray.700">
                          Deal Eligibility
                        </Text>
                        <Flex direction="column" gap={2}>
                          {dealTypeSwitches.map((switchConfig) => (
                            <Flex key={switchConfig.name} justifyContent="space-between">
                              <FormLabel fontSize="sm" mb={0}>
                                {switchConfig.label}
                              </FormLabel>
                              <Box>
                                <Switch
                                  isNoYes
                                  noLabel="No"
                                  yesLabel="Yes"
                                  size="sm"
                                  noYesFontSize="sm"
                                  name={switchConfig.name}
                                />
                              </Box>
                            </Flex>
                          ))}
                        </Flex>
                      </Box>

                      <Divider />
                    </>
                  ) : null}

                  {/* Working Hours */}
                  <WorkingHoursTable />
                </>
              ) : null}
            </Flex>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default UserSettingsModal;
